/*
 * File: CustomPassword.tsx
 * Project: a-a
 * File Created: Tuesday, 22nd October 2024 12:15:08 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Thursday, 11th September 2025 1:09:20 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */
import React, { ClipboardEvent } from 'react';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import zeplinColor from '../../../theme/Color';
import { styled } from '@mui/material/styles';
import './CustomPassword.css';

type CustomPasswordProps = {
  showPassword: boolean;
  style?: React.CSSProperties | undefined;
  label: string;
  name: string;
  text: string;
  width?: string;
  error?: string | boolean;
  onBlur?: () => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleClickShowPassword: () => void;
  handleMouseDownPassword: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleMouseUpPassword: (event: React.MouseEvent<HTMLButtonElement>) => void;
  tabindex?: number;
  disabled?: boolean;
};

const CustomOutlinedInput = styled(OutlinedInput)(({ theme }) => ({
  '&.MuiFormControl-root': {
    // border: `1px solid ${zeplinColor.Error}`,
    '& .MuiFormLabel-root.MuiInputLabel-root:not(.Mui-error)': {
      color: zeplinColor.Neutral9,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'green', // Change border color on focus
    },
    '& .MuiInputBase-root.MuiOutlinedInput-root.Mui-error': {
      border: `none`,
      '& .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${zeplinColor.Error}`,
      },
    },
    '& .MuiInputBase-root.MuiOutlinedInput-root:not(.Mui-error),': {
      border: `none`,
      '& .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${zeplinColor.Neutral9}`,
      },
    },
  },
}));

const CustomPassword = ({
  showPassword,
  style,
  label,
  name,
  width,
  text,
  error,
  onBlur,
  onChange,
  handleClickShowPassword,
  handleMouseDownPassword,
  handleMouseUpPassword,
  tabindex,
  disabled,
}: CustomPasswordProps) => {
  const handleCopyPasteCut = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
  };

  return (
    <FormControl sx={style ? { ...style } : {}} variant='outlined'>
      <InputLabel
        htmlFor='outlined-adornment-password'
        style={{
          color: disabled ? zeplinColor.Neutral6 : error ? zeplinColor.Error : '',
        }}
      >
        {label}
      </InputLabel>
      <OutlinedInput
        id='outlined-adornment-password'
        // style={style ? {...style}:{}}
        disabled={disabled || false}
        onCopy={handleCopyPasteCut}
        onPaste={handleCopyPasteCut}
        onCut={handleCopyPasteCut}
        value={text}
        error={error ? true : false}
        onBlur={onBlur}
        onChange={onChange}
        label={label}
        name={name}
        type={showPassword ? 'text' : 'password'}
        tabIndex={tabindex}
        endAdornment={
          <InputAdornment position='end'>
            <IconButton
              aria-label='toggle password visibility'
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              onMouseUp={handleMouseUpPassword}
              edge='end'
            >
              {disabled ? (
                <img src='/assets/eye-closed-disabled.svg' />
              ) : showPassword ? (
                <img src='/assets/eye-open.svg' />
              ) : (
                <img src='/assets/eye-closed.svg' />
              )}
            </IconButton>
          </InputAdornment>
        }
      />
      {error && (
        <p
          style={{ textAlign: 'left', color: zeplinColor.Error, fontSize: 12, margin: '0px 14px' }}
        >
          {error}
        </p>
      )}
    </FormControl>
  );
};

export default CustomPassword;
